import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import {
  FileText, Book, FolderTree, Plus, Search, Filter, Upload, Download, Trash2, Edit,
  ChevronDown, ChevronRight, Check, X, Loader2, Users, BarChart3, Save, Eye,
  HelpCircle, BookOpen, Tag, AlertCircle, Sparkles, FileUp, CheckCheck
} from 'lucide-react';

const Content = ({ initialTab = 'questions' }) => {
  const user = authService.getCurrentUser();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  
  // Update activeTab when initialTab prop changes (route change)
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  
  // Check if user is manager/admin
  const isManager = user?.role === 'admin' || user?.role === 'content_manager' || 
    (user?.role === 'manager' && (user?.module_access || []).includes('content'));
  const isAgent = user?.role === 'content_agent';

  // Questions State
  const [questions, setQuestions] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionStats, setQuestionStats] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [agents, setAgents] = useState([]);
  
  // Filters
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAIExtract, setShowAIExtract] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  
  // Selection for bulk actions
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  
  // Books State
  const [books, setBooks] = useState([]);
  const [showAddBook, setShowAddBook] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [bookSearch, setBookSearch] = useState('');

  // Fetch data
  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'questions') {
      fetchQuestions();
    } else if (activeTab === 'books') {
      fetchBooks();
    }
  }, [activeTab, selectedSubject, selectedTopic, selectedDifficulty]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [subjectsRes, topicsRes] = await Promise.all([
        api.getContentSubjects(),
        api.getContentTopics()
      ]);
      setSubjects(subjectsRes.data);
      setTopics(topicsRes.data);
      
      if (isManager) {
        const [agentsRes, statsRes] = await Promise.all([
          api.getContentAgents(),
          api.getContentQuestionStats()
        ]);
        setAgents(agentsRes.data);
        setQuestionStats(statsRes.data);
      }
      
      await fetchQuestions();
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const params = {};
      if (selectedSubject) params.subject_id = selectedSubject;
      if (selectedTopic) params.topic_id = selectedTopic;
      if (selectedDifficulty) params.difficulty = selectedDifficulty;
      params.limit = 100;
      
      const response = await api.getContentQuestions(params);
      setQuestions(response.data.questions);
      setTotalQuestions(response.data.total);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const fetchBooks = async () => {
    try {
      const params = {};
      if (bookSearch) params.search = bookSearch;
      if (selectedSubject) params.subject_id = selectedSubject;
      
      const response = await api.getContentBooks(params);
      setBooks(response.data);
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await api.deleteContentQuestion(questionId);
      toast.success('Question deleted');
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete question');
    }
  };

  const handleAssignQuestions = async (agentId) => {
    if (selectedQuestions.length === 0) {
      toast.error('Please select questions to assign');
      return;
    }
    
    try {
      await api.assignContentQuestions(selectedQuestions, agentId);
      toast.success(`${selectedQuestions.length} questions assigned`);
      setSelectedQuestions([]);
      setShowAssignModal(false);
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign questions');
    }
  };

  const handleApproveSelected = async () => {
    if (selectedQuestions.length === 0) { toast.error('Select questions first'); return; }
    setApproving(true);
    try {
      const res = await api.approveContentQuestions(selectedQuestions, false);
      toast.success(res.data.message || `${res.data.approved} approved`);
      setSelectedQuestions([]);
      fetchQuestions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    } finally { setApproving(false); }
  };

  const handleApproveAllPending = async () => {
    if (!window.confirm('Approve every unapproved question and add them to the repository?')) return;
    setApprovingAll(true);
    try {
      const res = await api.approveContentQuestions([], true);
      toast.success(res.data.message || `${res.data.approved} approved`);
      setSelectedQuestions([]);
      fetchQuestions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    } finally { setApprovingAll(false); }
  };

  const toggleQuestionSelect = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const selectAllQuestions = () => {
    if (selectedQuestions.length === questions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questions.map(q => q.id));
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'hard': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="content-page" className="max-w-[1400px] mx-auto space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-headings text-slate-900">
            {activeTab === 'questions' && 'Question Repository'}
            {activeTab === 'books' && 'Book Repository'}
            {activeTab === 'subjects' && 'Subjects & Topics'}
          </h1>
          <p className="text-sm sm:text-base text-slate-600 font-body mt-1">
            {activeTab === 'questions' && 'Manage your question bank for content delivery'}
            {activeTab === 'books' && 'Organize and manage your book library'}
            {activeTab === 'subjects' && 'Configure subjects and topics for content organization'}
          </p>
        </div>
      </div>

      {/* Stats Cards (Manager View) - Only on questions tab */}
      {isManager && questionStats && activeTab === 'questions' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <HelpCircle className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{questionStats.total}</p>
                <p className="text-sm text-slate-500">Total Questions</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{questionStats.by_difficulty?.easy || 0}</p>
                <p className="text-sm text-slate-500">Easy</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertCircle className="text-yellow-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{questionStats.by_difficulty?.medium || 0}</p>
                <p className="text-sm text-slate-500">Medium</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <X className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{questionStats.by_difficulty?.hard || 0}</p>
                <p className="text-sm text-slate-500">Hard</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Sections */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Questions Tab */}
        {activeTab === 'questions' && (
          <div className="p-6">
            {/* Filters and Actions */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={selectedSubject}
                  onChange={(e) => {
                    setSelectedSubject(e.target.value);
                    setSelectedTopic('');
                  }}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="filter-subject"
                >
                  <option value="">All Subjects</option>
                  {subjects.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                
                <select
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="filter-topic"
                  disabled={!selectedSubject}
                >
                  <option value="">All Topics</option>
                  {topics.filter(t => t.subject_id === selectedSubject).map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="filter-difficulty"
                >
                  <option value="">All Difficulty</option>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>

              {isManager && (
                <div className="flex items-center gap-2">
                  {selectedQuestions.length > 0 && (
                    <>
                      <button
                        onClick={handleApproveSelected}
                        disabled={approving}
                        className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-60"
                        data-testid="approve-selected-btn"
                      >
                        {approving ? <Loader2 size={14} className="animate-spin" /> : <Check size={16} />}
                        Approve ({selectedQuestions.length})
                      </button>
                      <button
                        onClick={() => setShowAssignModal(true)}
                        className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 flex items-center gap-2"
                      >
                        <Users size={16} />
                        Assign ({selectedQuestions.length})
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleApproveAllPending}
                    disabled={approvingAll}
                    className="px-3 py-2 border border-emerald-300 bg-emerald-50 text-emerald-700 rounded-lg text-sm hover:bg-emerald-100 flex items-center gap-2 disabled:opacity-60"
                    data-testid="approve-all-pending-btn"
                    title="Approve every unapproved question at once"
                  >
                    {approvingAll ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={16} />}
                    Approve All Pending
                  </button>
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 flex items-center gap-2"
                    data-testid="import-questions-btn"
                  >
                    <Upload size={16} />
                    Import
                  </button>
                  <button
                    onClick={() => setShowAIExtract(true)}
                    className="px-3 py-2 bg-gradient-to-r from-fuchsia-600 to-violet-600 text-white rounded-lg text-sm hover:from-fuchsia-500 hover:to-violet-500 flex items-center gap-2 shadow"
                    data-testid="ai-extract-btn"
                    title="Extract MCQs from an uploaded typing PPT using AI"
                  >
                    <Sparkles size={16} />
                    Import from PPT (AI)
                  </button>
                  <button
                    onClick={() => setShowAddQuestion(true)}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"
                    data-testid="add-question-btn"
                  >
                    <Plus size={16} />
                    Add Question
                  </button>
                </div>
              )}
            </div>

            {/* Questions Table */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {isManager && (
                      <th className="w-10 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedQuestions.length === questions.length && questions.length > 0}
                          onChange={selectAllQuestions}
                          className="rounded"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Question</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Subject / Topic</th>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase text-slate-500">Difficulty</th>
                    {isManager && (
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Assigned To</th>
                    )}
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {questions.length === 0 ? (
                    <tr>
                      <td colSpan={isManager ? 6 : 4} className="px-4 py-12 text-center text-slate-500">
                        <HelpCircle size={40} className="mx-auto mb-3 text-slate-300" />
                        <p className="font-medium">No questions found</p>
                        <p className="text-sm mt-1">Add questions to get started</p>
                      </td>
                    </tr>
                  ) : (
                    questions.map((q) => (
                      <tr key={q.id} className="hover:bg-slate-50">
                        {isManager && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedQuestions.includes(q.id)}
                              onChange={() => toggleQuestionSelect(q.id)}
                              className="rounded"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <p className="text-sm text-slate-900 line-clamp-3 whitespace-pre-line">{q.question_text}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {q.approved ? (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`approved-badge-${q.id}`}>
                                <Check size={10} /> Approved
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                Pending review
                              </span>
                            )}
                            {q.source === 'ai_ppt_extraction' && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200">
                                <Sparkles size={10} /> AI
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-slate-900">{q.subject_name}</p>
                          <p className="text-xs text-slate-500">{q.topic_name}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(q.difficulty)}`}>
                            {q.difficulty}
                          </span>
                        </td>
                        {isManager && (
                          <td className="px-4 py-3">
                            {q.assigned_to_name ? (
                              <span className="text-sm text-slate-600">{q.assigned_to_name}</span>
                            ) : (
                              <span className="text-sm text-slate-400">Unassigned</span>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {isManager && (
                              <>
                                <button
                                  onClick={() => setEditingQuestion(q)}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Edit"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteQuestion(q.id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            <p className="mt-4 text-sm text-slate-500">
              Showing {questions.length} of {totalQuestions} questions
            </p>
          </div>
        )}

        {/* Books Tab */}
        {activeTab === 'books' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search books..."
                  value={bookSearch}
                  onChange={(e) => setBookSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchBooks()}
                  className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64"
                  data-testid="book-search"
                />
              </div>
              
              {isManager && (
                <button
                  onClick={() => setShowAddBook(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"
                  data-testid="add-book-btn"
                >
                  <Plus size={16} />
                  Add Book
                </button>
              )}
            </div>

            {/* Books Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {books.length === 0 ? (
                <div className="col-span-full py-12 text-center text-slate-500">
                  <Book size={40} className="mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No books found</p>
                  <p className="text-sm mt-1">Add books to build your library</p>
                </div>
              ) : (
                books.map((book) => (
                  <BookCard 
                    key={book.id} 
                    book={book} 
                    isManager={isManager}
                    onEdit={() => setEditingBook(book)}
                    onRefresh={fetchBooks}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* Subjects & Topics Tab */}
        {activeTab === 'subjects' && isManager && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Manage Subjects & Topics</h2>
              <button
                onClick={() => setShowAddSubject(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"
                data-testid="add-subject-btn"
              >
                <Plus size={16} />
                Add Subject
              </button>
            </div>

            <div className="space-y-4">
              {subjects.map((subject) => (
                <SubjectCard
                  key={subject.id}
                  subject={subject}
                  topics={topics.filter(t => t.subject_id === subject.id)}
                  onAddTopic={() => {
                    setSelectedSubject(subject.id);
                    setShowAddTopic(true);
                  }}
                  onRefresh={fetchInitialData}
                />
              ))}
              
              {subjects.length === 0 && (
                <div className="py-12 text-center text-slate-500">
                  <FolderTree size={40} className="mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No subjects yet</p>
                  <p className="text-sm mt-1">Create subjects to organize your questions</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Question Modal */}
      {(showAddQuestion || editingQuestion) && (
        <QuestionModal
          question={editingQuestion}
          subjects={subjects}
          topics={topics}
          onClose={() => {
            setShowAddQuestion(false);
            setEditingQuestion(null);
          }}
          onSave={() => {
            setShowAddQuestion(false);
            setEditingQuestion(null);
            fetchQuestions();
          }}
        />
      )}

      {/* AI Extract from PPT Modal */}
      {showAIExtract && (
        <AIExtractModal
          subjects={subjects}
          onClose={() => setShowAIExtract(false)}
          onImported={() => { setShowAIExtract(false); fetchQuestions(); }}
        />
      )}

      {/* Add Subject Modal */}
      {showAddSubject && (
        <AddSubjectModal
          onClose={() => setShowAddSubject(false)}
          onSave={() => {
            setShowAddSubject(false);
            fetchInitialData();
          }}
        />
      )}

      {/* Add Topic Modal */}
      {showAddTopic && (
        <AddTopicModal
          subjectId={selectedSubject}
          subjects={subjects}
          onClose={() => setShowAddTopic(false)}
          onSave={() => {
            setShowAddTopic(false);
            fetchInitialData();
          }}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportQuestionsModal
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            fetchQuestions();
            fetchInitialData();
          }}
        />
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <AssignModal
          agents={agents}
          count={selectedQuestions.length}
          onClose={() => setShowAssignModal(false)}
          onAssign={handleAssignQuestions}
        />
      )}

      {/* Add/Edit Book Modal */}
      {(showAddBook || editingBook) && (
        <BookModal
          book={editingBook}
          subjects={subjects}
          onClose={() => {
            setShowAddBook(false);
            setEditingBook(null);
          }}
          onSave={() => {
            setShowAddBook(false);
            setEditingBook(null);
            fetchBooks();
          }}
        />
      )}
    </div>
  );
};

// ============ Sub Components ============

const BookCard = ({ book, isManager, onEdit, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloading, setDownloading] = useState(false);

  const handleUploadFile = async (e, formatHint) => {
    const file = e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 1024 * 1024 * 1024; // 1 GB
    if (file.size > MAX_SIZE) {
      toast.error('File too large. Maximum size is 1 GB.');
      if (e.target) e.target.value = '';
      return;
    }

    // Anything above 50 MB goes through chunked upload so we bypass the 100 MB proxy cap.
    const useChunked = file.size > 50 * 1024 * 1024;

    setUploading(true);
    setUploadProgress(0);
    try {
      if (useChunked) {
        await api.uploadBookFileChunked(book.id, file, formatHint, (pct) => setUploadProgress(pct));
      } else {
        await api.uploadBookFile(book.id, file, formatHint);
      }
      toast.success(`${formatHint === 'OPEN' ? 'Open file' : 'File'} uploaded successfully`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // reset the input so the same file can be re-uploaded if needed
      if (e.target) e.target.value = '';
    }
  };

  const handleDownload = async (fileInfo) => {
    try {
      const response = await api.downloadBookFile(book.id, fileInfo.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileInfo.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      const response = await api.downloadAllBookFiles(book.id);
      // Check if response is actually a blob/zip (not an error HTML page)
      if (response.data.size === 0) {
        toast.error('No files available to download');
        return;
      }
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${book.title}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      const detail = error.response?.data;
      if (typeof detail === 'object' && detail?.detail) {
        toast.error(detail.detail);
      } else if (error.response?.status === 404) {
        toast.error('No files found for this book');
      } else {
        toast.error('Download failed. Please try again.');
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await api.deleteBookFile(book.id, fileId);
      toast.success('File deleted');
      onRefresh();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this book and all its files?')) return;
    try {
      await api.deleteContentBook(book.id);
      toast.success('Book deleted');
      onRefresh();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const hasFiles = (book.files || []).length > 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow" data-testid={`book-card-${book.id}`}>
      {/* Cover Image */}
      <div className="h-40 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        {(book.cover_url || book.cover_path) ? (
          <img 
            src={book.cover_url || api.getBookCoverUrl(book.id)} 
            alt={book.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
            }}
          />
        ) : null}
        <div className={`items-center justify-center ${(book.cover_url || book.cover_path) ? 'hidden' : 'flex'}`}>
          <BookOpen size={48} className="text-slate-300" />
        </div>
      </div>
      
      <div className="p-4">
        <h3 className="font-semibold text-slate-900 line-clamp-1">{book.title}</h3>
        {book.author && <p className="text-sm text-slate-500">{book.author}</p>}
        
        {/* File Formats */}
        <div className="mt-3 flex flex-wrap gap-1">
          {(book.files || []).map((file) => {
            const isOpen = file.format === 'OPEN';
            return (
              <span
                key={file.id}
                className={`px-2 py-0.5 text-xs rounded cursor-pointer ${isOpen ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 font-semibold' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                onClick={() => handleDownload(file)}
                title={isOpen ? `Download source / open file (${file.filename})` : `Download ${file.format}`}
                data-testid={`book-file-pill-${file.id}`}
              >
                {isOpen ? `OPEN · ${(file.filename || '').split('.').pop().toUpperCase()}` : file.format}
              </span>
            );
          })}
        </div>

        {/* Actions */}
        {isManager && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <label className="flex-1 min-w-[100px] px-3 py-1.5 border border-slate-200 rounded text-xs text-center cursor-pointer hover:bg-slate-50">
              <input
                type="file"
                className="hidden"
                accept=".pdf,.epub,.mobi,.docx,.doc,.txt,.rtf,.azw3,.djvu,.zip,.rar,.7z"
                onChange={(e) => handleUploadFile(e)}
                disabled={uploading}
                data-testid={`add-book-file-${book.id}`}
              />
              {uploading && !uploadProgress ? 'Uploading...' :
                uploading ? `Uploading ${uploadProgress}%` : '+ Add File'}
            </label>
            <label
              className="flex-1 min-w-[100px] px-3 py-1.5 border border-amber-300 bg-amber-50 rounded text-xs text-center cursor-pointer hover:bg-amber-100 text-amber-800 font-medium"
              title="Upload the source / open file (InDesign, AI, PSD, DOCX, ZIP, RAR, 7Z, etc.) used to produce this book"
            >
              <input
                type="file"
                className="hidden"
                accept=".zip,.rar,.7z,.indd,.ai,.psd,.docx,.doc,.pdf,.pptx,.ppt,.xlsx,.xls,.epub,.mobi,.azw3,.djvu,.rtf,.txt"
                onChange={(e) => handleUploadFile(e, 'OPEN')}
                disabled={uploading}
                data-testid={`add-book-open-file-${book.id}`}
              />
              {uploading && !uploadProgress ? 'Uploading...' :
                uploading ? `Uploading ${uploadProgress}%` : '+ Open File'}
            </label>
            {hasFiles && (
              <button
                onClick={handleDownloadAll}
                disabled={downloading}
                className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                title="Download All (ZIP)"
                data-testid={`download-all-btn-${book.id}`}
              >
                <Download size={16} />
              </button>
            )}
            <button
              onClick={onEdit}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={handleDelete}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const SubjectCard = ({ subject, topics, onAddTopic, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);

  const handleDeleteSubject = async () => {
    if (topics.length > 0) {
      toast.error('Delete all topics first');
      return;
    }
    if (!window.confirm('Delete this subject?')) return;
    
    try {
      await api.deleteContentSubject(subject.id);
      toast.success('Subject deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Delete this topic?')) return;
    try {
      await api.deleteContentTopic(topicId);
      toast.success('Topic deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div 
        className="px-4 py-3 bg-slate-50 flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <span className="font-medium text-slate-900">{subject.name}</span>
          <span className="text-sm text-slate-500">({topics.length} topics)</span>
        </div>
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <button
            onClick={onAddTopic}
            className="px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
          >
            + Add Topic
          </button>
          <button
            onClick={handleDeleteSubject}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      
      {expanded && topics.length > 0 && (
        <div className="p-4 space-y-2">
          {topics.map(topic => (
            <div key={topic.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded">
              <span className="text-sm text-slate-700">{topic.name}</span>
              <button
                onClick={() => handleDeleteTopic(topic.id)}
                className="p-1 text-red-500 hover:bg-red-50 rounded"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const QuestionModal = ({ question, subjects, topics, onClose, onSave }) => {
  const [form, setForm] = useState({
    question_text: question?.question_text || '',
    options: question?.options || ['', '', '', ''],
    correct_option: question?.correct_option || 0,
    explanation: question?.explanation || '',
    subject_id: question?.subject_id || '',
    topic_id: question?.topic_id || '',
    difficulty: question?.difficulty || 'medium'
  });
  const [saving, setSaving] = useState(false);

  const filteredTopics = topics.filter(t => t.subject_id === form.subject_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.question_text || !form.subject_id || !form.topic_id) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (form.options.some(o => !o.trim())) {
      toast.error('All options are required');
      return;
    }

    setSaving(true);
    try {
      if (question) {
        await api.updateContentQuestion(question.id, form);
        toast.success('Question updated');
      } else {
        await api.createContentQuestion(form);
        toast.success('Question created');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold">{question ? 'Edit Question' : 'Add Question'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Question *</label>
            <textarea
              value={form.question_text}
              onChange={(e) => setForm({ ...form, question_text: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              placeholder="Enter the question"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {form.options.map((opt, idx) => (
              <div key={idx}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Option {String.fromCharCode(65 + idx)} *
                  {form.correct_option === idx && (
                    <span className="ml-2 text-green-600 text-xs">(Correct)</span>
                  )}
                </label>
                <div className="flex gap-2">
                  <textarea
                    value={opt}
                    onChange={(e) => {
                      const newOptions = [...form.options];
                      newOptions[idx] = e.target.value;
                      setForm({ ...form, options: newOptions });
                    }}
                    rows={2}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg resize-y"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, correct_option: idx })}
                    className={`px-3 py-2 rounded-lg ${
                      form.correct_option === idx 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    <Check size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Explanation (Optional)</label>
            <textarea
              value={form.explanation}
              onChange={(e) => setForm({ ...form, explanation: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              placeholder="Explain the answer"
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
              <select
                value={form.subject_id}
                onChange={(e) => setForm({ ...form, subject_id: e.target.value, topic_id: '' })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                required
              >
                <option value="">Select Subject</option>
                {subjects.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Topic *</label>
              <select
                value={form.topic_id}
                onChange={(e) => setForm({ ...form, topic_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                required
                disabled={!form.subject_id}
              >
                <option value="">Select Topic</option>
                {filteredTopics.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Difficulty *</label>
              <select
                value={form.difficulty}
                onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                required
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddSubjectModal = ({ onClose, onSave }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    try {
      await api.createContentSubject({ name: name.trim() });
      toast.success('Subject created');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subject');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold">Add Subject</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Subject Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              placeholder="e.g., Mathematics, Science"
              required
            />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddTopicModal = ({ subjectId, subjects, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [selectedSubject, setSelectedSubject] = useState(subjectId || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !selectedSubject) return;

    setSaving(true);
    try {
      await api.createContentTopic({ name: name.trim(), subject_id: selectedSubject });
      toast.success('Topic created');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create topic');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold">Add Topic</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              required
            >
              <option value="">Select Subject</option>
              {subjects.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Topic Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              placeholder="e.g., Algebra, Thermodynamics"
              required
            />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ImportQuestionsModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setImporting(true);
    try {
      const response = await api.importContentQuestions(file);
      setResult(response.data);
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} questions imported`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold">Import Questions</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
            <p className="font-medium mb-2">Expected columns:</p>
            <p>question_text, option_a, option_b, option_c, option_d, correct_option (A/B/C/D), explanation, subject, topic, difficulty</p>
          </div>
          
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText size={24} className="text-blue-600" />
                <span>{file.name}</span>
                <button onClick={() => setFile(null)} className="text-red-500">
                  <X size={18} />
                </button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                />
                <Upload size={32} className="mx-auto mb-2 text-slate-400" />
                <p className="text-slate-600">Click to select CSV or Excel file</p>
              </label>
            )}
          </div>
          
          {result && (
            <div className={`p-4 rounded-lg ${result.imported > 0 ? 'bg-green-50' : 'bg-yellow-50'}`}>
              <p className="font-medium">{result.message}</p>
              {result.skipped > 0 && <p className="text-sm mt-1">Skipped: {result.skipped}</p>}
              {result.errors?.length > 0 && (
                <div className="mt-2 text-sm text-red-600">
                  {result.errors.slice(0, 3).map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
            </div>
          )}
          
          <div className="flex justify-end gap-3">
            <button onClick={result ? onSuccess : onClose} className="px-4 py-2 border border-slate-200 rounded-lg">
              {result ? 'Done' : 'Cancel'}
            </button>
            {!result && (
              <button
                onClick={handleImport}
                disabled={!file || importing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {importing ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {importing ? 'Importing...' : 'Import'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AssignModal = ({ agents, count, onClose, onAssign }) => {
  const [selectedAgent, setSelectedAgent] = useState('');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold">Assign Questions</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <p className="text-slate-600">Assign {count} question{count > 1 ? 's' : ''} to a content agent</p>
          
          <select
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg"
          >
            <option value="">Select Agent</option>
            {agents.map(a => (
              <option key={a.id} value={a.id}>{a.name} ({a.question_count || 0} questions)</option>
            ))}
          </select>
          
          {agents.length === 0 && (
            <p className="text-sm text-slate-500">No content agents available. Create agents first.</p>
          )}
          
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg">
              Cancel
            </button>
            <button
              onClick={() => onAssign(selectedAgent)}
              disabled={!selectedAgent}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              Assign
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BookModal = ({ book, subjects, onClose, onSave }) => {
  const [form, setForm] = useState({
    title: book?.title || '',
    author: book?.author || '',
    isbn: book?.isbn || '',
    subject_id: book?.subject_id || '',
    category: book?.category || '',
    description: book?.description || ''
  });
  const [coverFile, setCoverFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      if (coverFile) formData.append('cover_file', coverFile);

      if (book) {
        await api.updateContentBook(book.id, formData);
        toast.success('Book updated');
      } else {
        await api.createContentBook(formData);
        toast.success('Book created');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold">{book ? 'Edit Book' : 'Add Book'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Author</label>
              <input
                type="text"
                value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">ISBN</label>
              <input
                type="text"
                value={form.isbn}
                onChange={(e) => setForm({ ...form, isbn: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
              <select
                value={form.subject_id}
                onChange={(e) => setForm({ ...form, subject_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              >
                <option value="">Select Subject</option>
                {subjects.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
              <input
                type="text"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                placeholder="e.g., Textbook, Reference"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Cover Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setCoverFile(e.target.files[0])}
              className="w-full"
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------- AI Extract from PPT modal ----------------
const AIExtractModal = ({ subjects, onClose, onImported }) => {
  const [ppts, setPpts] = useState([]);
  const [q, setQ] = useState('');
  const [selectedPpt, setSelectedPpt] = useState(null);
  const [subjectId, setSubjectId] = useState('');
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);

  const load = async (search = '') => {
    setLoading(true);
    try {
      const res = await api.listTypingPptsForAI(search);
      setPpts(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load PPTs');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runExtract = async () => {
    if (!selectedPpt) { toast.error('Pick a PPT first'); return; }
    setExtracting(true);
    try {
      const res = await api.extractQuestionsFromPpt(selectedPpt.id, subjectId || null);
      const { imported, skipped } = res.data;
      if (imported > 0) {
        toast.success(`Imported ${imported} questions${skipped ? ` (${skipped} skipped)` : ''}. Review below.`);
        onImported();
      } else {
        toast.info(res.data.message || 'No questions found in this PPT');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI extraction failed');
    } finally { setExtracting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3" data-testid="ai-extract-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles size={18} className="text-fuchsia-600" /> Import questions from PPT
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Gemini 3 Flash extracts every MCQ, auto-categorises into a subject and assigns a difficulty tag.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          {/* Optional: force a subject */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') load(q); }}
                     placeholder="Search by title, filename, subject…"
                     className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg"
                     data-testid="ai-extract-search" />
            </div>
            <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    data-testid="ai-extract-subject">
              <option value="">AI picks subject (recommended)</option>
              {subjects.map((s) => <option key={s.id} value={s.id}>Force: {s.name}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40 text-slate-400"><Loader2 className="animate-spin" /></div>
          ) : (
            <div className="border border-slate-200 rounded-lg divide-y max-h-80 overflow-y-auto">
              {ppts.length === 0 && (
                <div className="p-6 text-center text-sm text-slate-500">
                  No PPTs found. Upload a .pptx in the Typing module first.
                </div>
              )}
              {ppts.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPpt(p)}
                  data-testid={`ai-extract-ppt-${p.id}`}
                  className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center justify-between gap-3 ${selectedPpt?.id === p.id ? 'bg-fuchsia-50' : ''}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{p.title || p.original_filename}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {p.subject_name || 'No subject'} · {(p.file_type || 'pptx').toUpperCase()} · {p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : ''}
                    </div>
                  </div>
                  {selectedPpt?.id === p.id && <Check size={16} className="text-fuchsia-600 shrink-0" />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-slate-50 flex items-center justify-between gap-2">
          <div className="text-xs text-slate-500">
            {selectedPpt ? <>Selected: <span className="font-medium text-slate-800">{selectedPpt.title || selectedPpt.original_filename}</span></> : 'Pick a PPT'}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
            <button
              onClick={runExtract}
              disabled={!selectedPpt || extracting}
              data-testid="ai-extract-run-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-600 to-violet-600 text-white text-sm hover:from-fuchsia-500 hover:to-violet-500 disabled:opacity-50"
            >
              {extracting ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
              {extracting ? 'Extracting…' : 'Extract & import'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Content;
